/**
 * ExportarPage - Página full-screen de exportação (Opção A)
 * 
 * Responsabilidades:
 * - Ler currentSaveData de sessionStorage
 * - Renderizar tabs: Rápido (formato/modo) + Avançado (todos os campos)
 * - Executar exportação com useExport hook (ÚNICO lugar)
 * - Ao concluir: navegar de volta para OrderDetailsWindow
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Keys, readWithAliases } from '../../lib/storage/keys';
import { storage } from '../../lib/storage/storage';
import { sendToTerminal } from '../../lib/terminalLogger';
import type { CurrentSaveData, ExportFormat } from '../ExportModal/types';
import { useExport } from '../ExportModal/hooks/useExport';
import { FormatSelector } from '../ExportModal/components/FormatSelector';
import { ModeSelector } from '../ExportModal/components/ModeSelector';
import { ExportTypeSelector } from '../ExportModal/components/ExportTypeSelector';
import { ExportResultModal } from '../ExportModal/components/ExportResultModal';
import AdvancedOptionsPanel from '../ExportModal/components/AdvancedOptionsPanel';
import { ProgressTracker } from '../ExportModal/components/ProgressTracker';
import { ResultSummary } from '../ExportModal/components/ResultSummary';
import { MODAL_STYLES } from '../ExportModal/constants';
import { Button, OrderItemTag } from '../ui';
import { useNotifications } from '../feedback/NotificationProvider';

interface ExportarPageProps {}

/**
 * Valida se os IDs obrigatórios estão presentes e são válidos
 */
function validateExportIds(data: CurrentSaveData | null): {
  valid: boolean;
  errors: string[];
  orcId: number | null;
  oitId: number | null;
} {
  const errors: string[] = [];
  let orcId: number | null = null;
  let oitId: number | null = null;

  if (!data) {
    errors.push('Dados de exportação não encontrados');
    return { valid: false, errors, orcId, oitId };
  }

  if (!data.pedido) {
    errors.push('Dados do pedido não encontrados');
    return { valid: false, errors, orcId, oitId };
  }

  // Validar orc_id
  const rawOrcId = data.pedido.orc_id;
  if (rawOrcId === undefined || rawOrcId === null) {
    errors.push('orc_id não encontrado');
  } else if (typeof rawOrcId === 'string') {
    const parsed = parseInt(rawOrcId, 10);
    if (isNaN(parsed) || parsed <= 0) {
      errors.push(`orc_id inválido: "${rawOrcId}"`);
    } else {
      orcId = parsed;
    }
  } else if (typeof rawOrcId === 'number') {
    if (rawOrcId <= 0) {
      errors.push(`orc_id inválido: ${rawOrcId}`);
    } else {
      orcId = rawOrcId;
    }
  } else {
    errors.push(`orc_id com tipo inesperado: ${typeof rawOrcId}`);
  }

  // Validar oit_id
  const rawOitId = data.pedido.oit_id;
  if (rawOitId === undefined || rawOitId === null) {
    errors.push('oit_id não encontrado');
  } else if (typeof rawOitId === 'string') {
    const parsed = parseInt(rawOitId, 10);
    if (isNaN(parsed) || parsed <= 0) {
      errors.push(`oit_id inválido: "${rawOitId}"`);
    } else {
      oitId = parsed;
    }
  } else if (typeof rawOitId === 'number') {
    if (rawOitId <= 0) {
      errors.push(`oit_id inválido: ${rawOitId}`);
    } else {
      oitId = rawOitId;
    }
  } else {
    errors.push(`oit_id com tipo inesperado: ${typeof rawOitId}`);
  }

  return {
    valid: errors.length === 0,
    errors,
    orcId,
    oitId
  };
}

export default function ExportarPage({}: ExportarPageProps) {
  const navigate = useNavigate();
  const { notify } = useNotifications();

  // Callback para voltar - usa histórico para retornar ao estado anterior (OrderDetails visível)
  const handleClose = useCallback(() => {
    sendToTerminal('⬅️ [EXPORT_PAGE] handleClose - navegando de volta', {
      historyLength: window.history.length,
      currentPath: window.location.pathname,
    });

    // ✅ CORREÇÃO: Usar navigate(-1) para voltar ao estado anterior
    // Isso permite que OrderDetails (que está hidden com display:none) volte a aparecer
    // Se não houver histórico, fallback para /pedidos
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/pedidos');
    }
  }, [navigate]);

  // ✅ CORREÇÃO: Usar useState ao invés de useMemo para permitir recarregar dados
  const [savedData, setSavedData] = useState<CurrentSaveData | null>(null);
  const [dataLoadAttempts, setDataLoadAttempts] = useState(0);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);

  // Carregar dados do storage com retry
  const loadSavedData = useCallback(() => {
    try {
      const data = readWithAliases<CurrentSaveData>(
        Keys.export.currentSaveData,
        ['currentSaveData'],
        { area: 'session', persistIfAliased: true }
      );

      // Validar IDs antes de aceitar os dados
      const validation = validateExportIds(data);

      if (!validation.valid) {
        sendToTerminal('⚠️ [EXPORT_PAGE] Dados inválidos no storage', {
          errors: validation.errors,
          attempt: dataLoadAttempts + 1,
          hasData: !!data,
          pedidoKeys: data?.pedido ? Object.keys(data.pedido) : []
        });

        // Se falhou, tentar novamente após delay (até 3 tentativas)
        if (dataLoadAttempts < 2) {
          setDataLoadAttempts(prev => prev + 1);
          return; // useEffect vai reagir e tentar novamente
        }

        setDataLoadError(`Dados inválidos: ${validation.errors.join(', ')}`);

        // Notificar usuário
        notify({
          type: 'error',
          title: 'Erro ao Carregar Dados',
          message: `Não foi possível carregar dados do pedido: ${validation.errors.join(', ')}`,
          sticky: true
        });
        return;
      }

      sendToTerminal('✅ [EXPORT_PAGE] Dados carregados com sucesso', {
        orcId: validation.orcId,
        oitId: validation.oitId,
        objetosCount: data?.objetos?.length || 0,
        attempt: dataLoadAttempts + 1
      });

      setSavedData(data);
      setDataLoadError(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      sendToTerminal('❌ [EXPORT_PAGE] Erro ao carregar dados', { error: errorMsg });
      setDataLoadError(errorMsg);

      notify({
        type: 'error',
        title: 'Erro ao Carregar Dados',
        message: `Erro inesperado: ${errorMsg}`,
        sticky: true
      });
    }
  }, [dataLoadAttempts, notify]);

  // Carregar dados na montagem e quando tentativas mudam
  useEffect(() => {
    // Delay progressivo para dar tempo do storage ser populado
    const delay = dataLoadAttempts * 300; // 0ms, 300ms, 600ms

    const timeoutId = setTimeout(() => {
      loadSavedData();
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [dataLoadAttempts, loadSavedData]);

  // ✅ Ler tipo de exportação já selecionado (evita mostrar seletor novamente)
  // CORREÇÃO: Usar 'undefined' como valor inicial para distinguir de "não carregado" vs "não encontrado"
  const [preSelectedExportType, setPreSelectedExportType] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    // ✅ CORREÇÃO: Pequeno delay para garantir que storage foi populado
    const timeoutId = setTimeout(() => {
      try {
        const type = readWithAliases<string>(
          Keys.export.selectedExportType,
          [],
          { area: 'session', persistIfAliased: false }
        );

        sendToTerminal('🔍 [EXPORT_PAGE] Leitura de tipo pré-selecionado', {
          found: !!type,
          type: type || 'none',
        });

        // Se encontrou tipo válido, usar; senão, marcar como null (não encontrado)
        setPreSelectedExportType(type || null);
      } catch (err) {
        sendToTerminal('⚠️ [EXPORT_PAGE] Erro ao ler tipo pré-selecionado', {
          error: err instanceof Error ? err.message : String(err),
        });
        setPreSelectedExportType(null); // Marcar como "não encontrado"
      }
    }, 50); // 50ms de delay para garantir que storage foi populado

    return () => clearTimeout(timeoutId);
  }, []);

  // Hook ÚNICO de exportação (remove duplicação)
  const {
    // estado
    status, format, mode, exportType, recommendedFormat, progress, result, error, cancelRequested, initError,
    productionError, layoutError,
    // handlers
    setFormat, setMode, setExportType, handleExport, handleCancel, handleRetry,
    // perfis
    profiles, activeProfile, setActiveProfile, loadingRules, rulesError, retryFetchRules,
    // avançado
    useOverrides, setUseOverrides, overrides, setOverride,
  } = useExport({ 
    savedData, 
    onComplete: (exportResult) => {
      // Mostrar modal de resultado
      setShowResultModal(true);
    }
  });

  // Estados para modals
  // ✅ CORREÇÃO BUG DUPLO CLIQUE: Iniciar como null (indefinido) até saber se há tipo pré-selecionado
  const [showTypeSelector, setShowTypeSelector] = useState<boolean | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);

  // ✅ CORREÇÃO: Flag para rastrear se já verificamos o tipo pré-selecionado
  const checkedPreSelectedRef = useRef(false);

  // ✅ CORREÇÃO BUG DUPLO CLIQUE: Aplicar tipo pré-selecionado E fechar seletor
  useEffect(() => {
    // Evitar múltiplas execuções
    if (checkedPreSelectedRef.current) return;

    // undefined = ainda carregando, aguardar
    if (preSelectedExportType === undefined) {
      return;
    }

    // Tipo válido encontrado - aplicar e fechar seletor
    if (preSelectedExportType && (preSelectedExportType === 'production' || preSelectedExportType === 'layout' || preSelectedExportType === 'both')) {
      checkedPreSelectedRef.current = true;
      setExportType(preSelectedExportType as 'production' | 'layout' | 'both');
      setShowTypeSelector(false); // ✅ CORREÇÃO: Fechar seletor porque já temos tipo
      sendToTerminal('🎯 [EXPORT_PAGE] Tipo pré-selecionado aplicado - seletor fechado', { type: preSelectedExportType });
      return;
    }

    // null = carregou mas não encontrou tipo - mostrar seletor
    checkedPreSelectedRef.current = true;
    setShowTypeSelector(true);
    sendToTerminal('📋 [EXPORT_PAGE] Sem tipo pré-selecionado - mostrando seletor');
  }, [preSelectedExportType, setExportType]);

  // Estado da tab ativa
  const [activeTab, setActiveTab] = useState<'rapido' | 'avancado'>('rapido');

  // Guarda o status atual para log de fechamento
  const latestStatusRef = useRef(status);
  useEffect(() => { latestStatusRef.current = status; }, [status]);

  // ✅ CORREÇÃO BUG DUPLO CLIQUE: Flag para controlar cleanup
  // Só limpar storage quando o tipo já foi aplicado (evita limpar no StrictMode)
  const typeWasAppliedRef = useRef(false);

  // Atualizar flag quando tipo é aplicado
  useEffect(() => {
    if (checkedPreSelectedRef.current && showTypeSelector === false) {
      typeWasAppliedRef.current = true;
    }
  }, [showTypeSelector]);

  // Log de abertura/fechamento + limpeza em mount/unmount
  useEffect(() => {
    try {
      sendToTerminal('[EXPORT_PAGE:OPENED]', {
        hasData: !!savedData,
        objectsCount: savedData?.objetos?.length || 0,
        orcId: savedData?.pedido?.orc_id,
        oitId: savedData?.pedido?.oit_id,
        preSelectedType: preSelectedExportType || 'none',
      });
    } catch {}

    return () => {
      try {
        sendToTerminal('[EXPORT_PAGE:CLOSED]', {
          status: latestStatusRef.current,
          typeWasApplied: typeWasAppliedRef.current
        });
        // ✅ CORREÇÃO: Só limpar se o tipo foi efetivamente aplicado
        // Isso evita limpar no unmount do StrictMode (que acontece antes de ler)
        if (typeWasAppliedRef.current) {
          storage.remove(Keys.export.selectedExportType, 'session');
          sendToTerminal('🧹 [EXPORT_PAGE] Tipo de exportação limpo do storage');
        }
      } catch {}
    };
  }, []); // mount/unmount apenas

  // Loading state: enquanto carrega dados do storage
  if (!savedData && !dataLoadError && dataLoadAttempts <= 2) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-navy-600 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-white">Carregando dados...</div>
            <div className="text-sm text-gray-400 mt-1">
              {dataLoadAttempts > 0 && `Tentativa ${dataLoadAttempts + 1} de 3`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Validação: erro ao carregar dados
  if (dataLoadError) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="text-xl font-semibold text-red-400">Erro ao Carregar Dados</div>
          <div className="text-sm text-gray-300">{dataLoadError}</div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setDataLoadError(null);
                setDataLoadAttempts(0);
              }}
              className="flex-1 px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white"
            >
              Tentar Novamente
            </button>
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Validação: se hook retornou erro de inicialização, mostrar erro
  if (initError) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="text-xl font-semibold text-red-400">Erro de Inicialização</div>
          <div className="text-sm text-gray-300">{initError}</div>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Validação: se não há dados, mostrar erro
  if (!savedData) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="text-xl font-semibold text-red-400">Erro ao carregar dados</div>
          <div className="text-sm text-gray-300">
            Nenhum dado de exportação foi encontrado. Por favor, retorne e tente novamente.
          </div>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Validação: se não há objetos, mostrar erro
  if (!savedData.objetos || savedData.objetos.length === 0) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="text-xl font-semibold text-yellow-400">Nenhum objeto disponível</div>
          <div className="text-sm text-gray-300">
            Não há objetos para exportar. Verifique se o documento foi salvo corretamente.
          </div>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Determinar se está em progresso ou concluído
  const isExporting = status === 'exporting' || status === 'preparing' || status === 'exporting-layout';
  const isComplete = status === 'complete';
  const hasError = status === 'error';

  // ✅ CORREÇÃO BUG DUPLO CLIQUE: Aguardar definição do showTypeSelector
  // Enquanto showTypeSelector === null, estamos verificando se há tipo pré-selecionado
  if (showTypeSelector === null) {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 space-y-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-navy-600 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-white">Preparando exportação...</div>
            <div className="text-sm text-gray-400 mt-1">Verificando configurações</div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar modal de seleção de tipo (se ainda não escolheu)
  if (showTypeSelector === true) {
    return (
      <ExportTypeSelector
        visible={true}
        onSelect={(type) => {
          sendToTerminal('[EXPORT_PAGE] Tipo de exportação selecionado', { type });
          setExportType(type);
          setShowTypeSelector(false);
        }}
        onCancel={() => {
          sendToTerminal('[EXPORT_PAGE] Cancelou seleção de tipo');
          handleClose();
        }}
      />
    );
  }

  // Renderizar modal de resultado (quando completar)
  if (showResultModal && result) {
    return (
      <ExportResultModal
        visible={true}
        result={result}
        onClose={() => {
          sendToTerminal('[EXPORT_PAGE] Fechando modal de resultado - exportação concluída');
          setShowResultModal(false);

          // ✅ CORREÇÃO: Após exportação completa, voltar para lista de pedidos (não para OrderDetails)
          // Emitir evento para fechar OrderDetails em ProductionOrdersPage/SalesOrdersPage
          window.dispatchEvent(new CustomEvent('export:completed', {
            detail: { success: result.success, partialSuccess: result.partialSuccess }
          }));

          // Navegar diretamente para /pedidos (não usar navigate(-1) que voltaria para OrderDetails)
          setTimeout(() => {
            navigate('/pedidos');
          }, 300);
        }}
      />
    );
  }
  const isIdle = status === 'idle';

  return (
    <div className="h-screen w-full bg-navy-900 flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-navy-700 flex items-center justify-between bg-navy-800">
        <div>
          <h1 className="text-white text-lg font-semibold">Exportação de Arquivos</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-400">{savedData.objetos.length} objeto(s)</span>
            {savedData.pedido?.orc_id && savedData.pedido?.oit_id && (
              <OrderItemTag 
                orderId={savedData.pedido.orc_id} 
                itemId={savedData.pedido.oit_id} 
                size="xs" 
                variant="compact" 
              />
            )}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleClose}
          disabled={isExporting}
        >
          {isExporting ? 'Aguarde...' : '← Voltar'}
        </Button>
      </div>

      {/* Tabs */}
      {isIdle && (
        <div className="px-6 pt-4 border-b border-navy-700 bg-navy-800">
          <div className="flex items-center gap-2 text-xs">
            <button 
              onClick={() => setActiveTab('rapido')} 
              className={`px-3 py-1.5 rounded-t transition-all font-medium ${
                activeTab === 'rapido' 
                  ? 'bg-navy-900 text-white border-b-2 border-blue-500' 
                  : 'bg-navy-700 text-gray-300 hover:bg-navy-600 hover:text-white'
              }`}
            >
              ⚡ Exportação Rápida
            </button>
            <button 
              onClick={() => setActiveTab('avancado')} 
              className={`px-3 py-1.5 rounded-t transition-all font-medium ${
                activeTab === 'avancado' 
                  ? 'bg-navy-900 text-white border-b-2 border-blue-500' 
                  : 'bg-navy-700 text-gray-300 hover:bg-navy-600 hover:text-white'
              }`}
            >
              ⚙️ Configuração Avançada
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Tab: Rápido */}
        {isIdle && activeTab === 'rapido' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <FormatSelector 
              value={format} 
              onChange={setFormat} 
              recommendedFormat={recommendedFormat} 
              disabled={isExporting} 
            />
            
            <ModeSelector 
              value={mode} 
              onChange={setMode} 
              disabled={isExporting} 
            />

            {/* Perfis */}
            {loadingRules && (
              <div className="space-y-2 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-32"></div>
                <div className="h-10 bg-gray-700 rounded"></div>
              </div>
            )}

            {rulesError && (
              <div className="text-red-400 bg-red-900/30 p-3 rounded text-sm">
                {rulesError}
                <button className="ml-2 underline" onClick={retryFetchRules}>Tentar novamente</button>
              </div>
            )}

            {!loadingRules && !rulesError && profiles && profiles.length === 0 && (
              <div className="text-sm text-yellow-400 bg-yellow-900/20 p-3 rounded border border-yellow-700">
                ⚠️ Nenhum perfil de exportação encontrado. Usando configurações padrão do sistema.
              </div>
            )}

            {!loadingRules && !rulesError && profiles && profiles.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Perfil de exportação</label>
                <select
                  className={MODAL_STYLES.select}
                  value={activeProfile?.id ?? profiles[0]?.id}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const p = profiles.find(x => x.id === id) || null;
                    setActiveProfile(p as any);
                  }}
                >
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400">
                  O perfil define configurações padrão. Você pode personalizar na aba "Avançado".
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Avançado */}
        {isIdle && activeTab === 'avancado' && (
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Info sobre perfil ativo - minimalista */}
            <div className="bg-blue-900/10 border-l-2 border-blue-600 pl-2 py-1.5 rounded">
              <p className="text-[11px] text-blue-300/70">
                Perfil: <span className="font-medium">{activeProfile?.name || 'Padrão do Sistema'}</span>
                {useOverrides && (
                  <button 
                    onClick={() => { 
                      setUseOverrides(false); 
                      setOverride('', {});
                    }} 
                    className="ml-2 text-blue-400 hover:text-blue-300 underline"
                  >
                    Resetar
                  </button>
                )}
              </p>
            </div>

            {/* Aviso quando há overrides ativos - minimalista */}
            {useOverrides && (
              <div className="bg-yellow-900/10 border-l-2 border-yellow-600 pl-2 py-1.5 rounded">
                <p className="text-[11px] text-yellow-300/70">
                  ⚠️ Overrides ativos nesta exportação
                </p>
              </div>
            )}

            <AdvancedOptionsPanel
              format={format as ExportFormat}
              useOverrides={!!useOverrides}
              overrides={overrides as any}
              onToggleUseOverrides={setUseOverrides}
              onOverrideChange={setOverride}
              profileOptions={((activeProfile as any)?.options || {})[format]}
            />
          </div>
        )}

        {/* Progresso de Exportação */}
        {/* Exportando - Loading Simples (C# já tem progressbar) */}
        {isExporting && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-navy-800 rounded-lg p-8 border border-navy-700">
              <div className="flex flex-col items-center justify-center space-y-4">
                {/* Spinner */}
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-navy-600 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
                
                {/* Mensagem */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {status === 'preparing' && 'Preparando exportação...'}
                    {status === 'exporting' && 'Exportando arquivos...'}
                    {status === 'exporting-layout' && 'Exportando layout PNG...'}
                  </h3>
                  <p className="text-sm text-gray-400">
                    O CorelDRAW está processando. Aguarde...
                  </p>
                </div>

                {/* Botão Cancelar (se houver suporte) */}
                {!cancelRequested && (
                  <button
                    onClick={handleCancel}
                    className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                  >
                    Cancelar
                  </button>
                )}
                {cancelRequested && (
                  <p className="text-sm text-yellow-400">Solicitando cancelamento...</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resultado */}
        {isComplete && result && (
          <div className="max-w-2xl mx-auto">
            <ResultSummary result={result} handleClose={handleClose} />
          </div>
        )}

        {/* Erro */}
        {hasError && (
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="text-red-400 bg-red-900/30 p-4 rounded text-sm">
              <div className="font-semibold mb-2">Erro na exportação</div>
              <div>{error}</div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleRetry} 
                className={MODAL_STYLES.buttonPrimary}
              >
                Tentar novamente
              </button>
              <button 
                onClick={handleClose} 
                className={MODAL_STYLES.buttonSecondary}
              >
                Voltar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {isIdle && (
        <div className="p-4 border-t border-navy-700 bg-navy-800 flex items-center justify-end gap-2">
          <button 
            onClick={handleClose} 
            className="px-3 py-1.5 bg-navy-700 hover:bg-navy-600 text-gray-200 rounded text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button 
            onClick={handleExport} 
            className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-navy-900 rounded text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
            disabled={isExporting}
          >
            Exportar Agora
            <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
}


